import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

import { ReportFormat } from '../types';

export type GenerateSuitabilityReportPayload = {
  reportTemplate?: string;
  supportingDataUploadFileIds?: string[];
  feeDocumentFileIds?: string[];
  appendixFileIds?: number[];
  temporaryAppendixFileIds?: number[];
  lastReviewDate?: string;
  reportFormat?: ReportFormat;
  addTitlePage?: boolean;
  addTableOfContents?: boolean;
  useDeepThinking?: boolean;
};

type GenerateSuitabilityReportResponse = {
  taskId: string;
  suitabilityReportId: string;
  fileOwnerClientId: string;
};

type MutationInput = {
  resource: string;
  values: GenerateSuitabilityReportPayload;
};

export function useGenerateSuitabilityReport() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: GenerateSuitabilityReportResponse }, Error, MutationInput>({
    mutationFn: ({ resource, values }: MutationInput) =>
      api.post<GenerateSuitabilityReportResponse>(resource, values),
    onSettled: (response, _error, { resource }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      const [scope, id] = resource.split('/');
      queryClient.invalidateQueries({ queryKey: [scope, id, 'all-documents'] });
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ['clients', response.data.fileOwnerClientId, 'suitability-reports'],
        });
      }
    },
  });

  const generateClientSuitabilityReport = async (
    clientId: string,
    values: GenerateSuitabilityReportPayload,
  ) => {
    return mutateAsync({ resource: `clients/${clientId}/suitability-reports`, values });
  };

  const generateFamilySuitabilityReport = async (
    familyId: string,
    values: GenerateSuitabilityReportPayload,
  ) => {
    return mutateAsync({
      resource: `families/${familyId}/suitability-reports`,
      values,
    });
  };

  const generateSuitabilityReport = async (
    id: string,
    type: 'client' | 'family',
    values: GenerateSuitabilityReportPayload,
  ) => {
    if (type === 'client') {
      return generateClientSuitabilityReport(id, values);
    } else {
      return generateFamilySuitabilityReport(id, values);
    }
  };

  return {
    generateSuitabilityReport,
    error,
    isLoading,
    reset,
  };
}
